import { CommonModule } from '@angular/common';
import {
	Component,
	Input,
	ViewChild,
	ElementRef,
	AfterViewInit,
	SimpleChanges,
	OnChanges,
} from '@angular/core';
import sanitize from 'sanitize-html';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

const FACTOR_ID_SELECTOR = 'data-factorid';
const SPAN_HIGHLIGHTED_SELECTOR = 'data-highlighted';
const FALLBACK_HIGHLIGHT_COLOR = 'yellow';

@Component({
	selector: 'app-annotated-text',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './annotated-text.component.html',
	styleUrls: ['./annotated-text.component.css'],
})
export class AnnotatedTextComponent implements AfterViewInit, OnChanges {
	@Input() annotatedText: string = '';
	@Input() selectedFactors: string[] = [];
	@ViewChild('htmlContainer', { static: false }) htmlContainer!: ElementRef;
	safeHtml: SafeHtml = '';
	highlightColor: string = FALLBACK_HIGHLIGHT_COLOR;

	constructor(
		private sanitizer: DomSanitizer,
		private el: ElementRef,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['annotatedText']) {
			this.sanitizeHtml();
			setTimeout(() => {
				this.init();
			});
		}
		if (changes['selectedFactors']) {
			this.toggleHighlightedFactors();
		}
	}

	ngAfterViewInit(): void {
		this.init();
	}

	init() {
		this.toggleHighlightedFactors();
		this.highlightColor =
			getComputedStyle(this.el.nativeElement).getPropertyValue('--highlight-color') ||
			FALLBACK_HIGHLIGHT_COLOR;
	}

	private toggleHighlightedFactors() {
		if (this.htmlContainer) {
			this.removeExistingHighlights();
			if (this.selectedFactors.length > 0) {
				const selector = this.selectedFactors
					.map(id => `span[${FACTOR_ID_SELECTOR}="${id}"]`)
					.join(',');
				const spans = this.htmlContainer.nativeElement.querySelectorAll(selector);
				spans.forEach((span: HTMLElement) => {
					span.setAttribute(SPAN_HIGHLIGHTED_SELECTOR, 'true');
					span.style.backgroundColor = this.highlightColor;
					span.style.scrollMarginTop = '100px';
				});
				if (spans.length > 0) {
					spans[0].scrollIntoView({
						behavior: 'smooth',
						block: 'start',
					});
				}
			}
		}
	}

	private removeExistingHighlights() {
		this.htmlContainer.nativeElement
			.querySelectorAll(`span[${SPAN_HIGHLIGHTED_SELECTOR}="true"]`)
			.forEach((span: HTMLElement) => {
				span.removeAttribute(SPAN_HIGHLIGHTED_SELECTOR);
				span.removeAttribute('style');
				// span.style.backgroundColor = '';
			});
	}

	private sanitizeHtml() {
		const clean = sanitize(this.annotatedText, {
			allowedAttributes: {
				'*': [FACTOR_ID_SELECTOR, 'style'],
				a: ['href', 'target'],
				img: ['src', 'alt', 'style'],
			},
			allowedTags: sanitize.defaults.allowedTags.concat(['img']),
		});
		this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(clean);
	}
}
